// « Productivité » — the view of « Employés » (CreateAgent.js) that follows one
// person at a time: what they sent (AI draft as is, retouched, written by
// hand), their outcomes and reactivity, their queue, their rhythm.
// API (read): GET /api/employees/agents/basic, /api/statistics/employee/{id}/
// productivity | insights | performance (admin: /api/statistics/admin/
// productivity | insights), /api/employees/roster-stats (team comparator),
// /api/statistics/{employee/{id}|admin}/productivity/tickets (drill-down).
// Write: PUT /api/logistics/team-sla-config (« Objectifs »).
// A team member (role employee) sees their own numbers only (« Ma
// productivité »): GET /api/statistics/me/productivity and its drill-down
// /me/productivity/tickets — no member selector, no objectives, no insights.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, ChartColumn, ChevronRight, CircleCheck, Clock, Hourglass, ListChecks, Send, Smile, Sparkles,
  Target, Ticket, Timer, TriangleAlert,
} from 'lucide-react';
import {
  Badge, Banner, Card, Drawer, EmptyState, Field, Kpi, Modal, Pagination, Segmented,
  initialsOf,
} from '../design/ui';
import { getStatusLabel } from '../constants/ticketStatuses';
import {
  deltaPercent, fillDailyCounts, formatDays, formatDuration, formatHoursMinutes, formatNumber, moodTone,
  selfProductivityTiles,
} from './employeesModel';
import { sessionRole } from './accountScope';
import { formatShortDate, formatShortDateTime } from '../utils/dates';
import './EmployeeProductivity.css';

const CATEGORY_META = {
  ai_unmodified: { labelKey: 'employeeProductivity.aiUnmodified', labelFallback: 'IA validée telle quelle', field: 'ai_validated_unmodified', series: 1 },
  ai_modified: { labelKey: 'employeeProductivity.aiModified', labelFallback: 'IA retouchée avant envoi', field: 'ai_validated_modified', series: 2 },
  manual: { labelKey: 'employeeProductivity.manual', labelFallback: 'Rédigée manuellement', field: 'manual_messages', series: 3 },
};
const CATEGORIES = ['ai_unmodified', 'ai_modified', 'manual'];

const DRILLDOWN_PAGE_SIZE = 25;
const PERIODS = [7, 30, 90];

// Sentinel value in the selector for the admin's own productivity.
const ADMIN_OPTION_ID = '__admin__';

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

// Delta vs the previous window. For latencies lower is better.
const Delta = ({ current, previous, lowerIsBetter = false, t }) => {
  const pct = deltaPercent(current, previous);
  if (pct === null) return null;
  const improved = lowerIsBetter ? pct < 0 : pct > 0;
  return (
    <span
      className={`team-prod__delta team-prod__delta--${improved ? 'good' : 'bad'}`}
      title={improved
        ? t('teamStatsUi.prod.deltaBetter', 'Mieux que la période précédente ({{pct}} %)', { pct: pct > 0 ? `+${pct}` : pct })
        : t('teamStatsUi.prod.deltaWorse', 'Moins bien que la période précédente ({{pct}} %)', { pct: pct > 0 ? `+${pct}` : pct })}
    >
      <Badge tone={improved ? 'success' : 'danger'} icon={pct < 0 ? ArrowDown : ArrowUp}>{`${Math.abs(pct)} %`}</Badge>
    </span>
  );
};

const ProportionList = ({ rows, maxRows = 6 }) => {
  const top = rows.slice(0, maxRows);
  const max = top.reduce((m, r) => Math.max(m, r.count || 0), 0);
  if (top.length === 0 || max === 0) return null;
  return (
    <ul className="team-prod__props">
      {top.map((row) => (
        <li key={row.label} className="team-prod__prop" title={`${row.label} — ${formatNumber(row.count)}`}>
          <span className="team-prod__prop-label">{row.label}</span>
          <span className="u-meter" aria-hidden="true"><span style={{ width: `${(row.count / max) * 100}%` }} /></span>
          <span className="team-prod__prop-value u-num">
            {formatNumber(row.count)}
            {row.percentage != null && <em>{`${Math.round(row.percentage)} %`}</em>}
          </span>
        </li>
      ))}
    </ul>
  );
};

// UTC buckets from the API shifted to the viewer's clock (whole hours — a
// close approximation across DST, stated in the card).
const Heatmap = ({ cells, t }) => {
  const days = [
    t('teamStatsUi.prod.dayMon', 'Lun'), t('teamStatsUi.prod.dayTue', 'Mar'), t('teamStatsUi.prod.dayWed', 'Mer'),
    t('teamStatsUi.prod.dayThu', 'Jeu'), t('teamStatsUi.prod.dayFri', 'Ven'), t('teamStatsUi.prod.daySat', 'Sam'),
    t('teamStatsUi.prod.daySun', 'Dim'),
  ];
  const matrix = useMemo(() => {
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    const shift = Math.round(-new Date().getTimezoneOffset() / 60);
    (cells || []).forEach((cell) => {
      const raw = (cell.hour ?? 0) + shift;
      const hour = ((raw % 24) + 24) % 24;
      const dayShift = Math.floor(raw / 24);
      const dow = ((((cell.dow ?? 1) - 1 + dayShift) % 7) + 7) % 7;
      grid[dow][hour] += cell.count || 0;
    });
    const max = grid.reduce((m, row) => Math.max(m, ...row), 0);
    return { grid, max };
  }, [cells]);

  if (matrix.max === 0) return null;
  return (
    <div className="team-prod__heatmap" role="img" aria-label={t('employeeProductivity.heatmapAria', 'Messages envoyés par heure et jour')}>
      {matrix.grid.map((row, d) => (
        <div key={days[d]} className="team-prod__heatmap-row">
          <span className="team-prod__heatmap-day">{days[d]}</span>
          {row.map((count, h) => (
            <i
              key={h}
              className={count > 0 ? 'is-filled' : ''}
              style={count > 0 ? { opacity: 0.25 + 0.75 * (count / matrix.max) } : undefined}
              title={`${days[d]} ${String(h).padStart(2, '0')} h — ${formatNumber(count)}`}
            />
          ))}
        </div>
      ))}
      <div className="team-prod__heatmap-hours" aria-hidden="true">
        <span />
        <span className="team-prod__heatmap-scale">
          {[0, 6, 12, 18].map((h) => <b key={h} style={{ left: `${(h / 24) * 100}%` }}>{`${h} h`}</b>)}
        </span>
      </div>
    </div>
  );
};

const EmployeeProductivity = ({ embedded = false, initialEmployeeId = null, onEmployeeChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch (err) {
      return {};
    }
  }, []);
  const userRole = useMemo(() => sessionRole() || userData?.role, [userData]);
  const ownUserId = userData?._id || localStorage.getItem('user_id') || null;

  const canBrowseEmployees = ['admin', 'seller', 'logistics', 'logistician', 'eshop'].includes(userRole);
  // The signed-in team member, on their own numbers (/api/statistics/me/*).
  const isSelf = userRole === 'employee';
  const canDrill = canBrowseEmployees || isSelf;

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(initialEmployeeId || '');
  // Period: 7/30/90 days (auto-reload) or 'custom' with explicit dates.
  const [period, setPeriod] = useState(7);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const startDate = period === 'custom' ? customStart : isoDaysAgo(period);
  const endDate = period === 'custom' ? customEnd : '';

  const [productivity, setProductivity] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [insights, setInsights] = useState(null);
  const [teamComparator, setTeamComparator] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // SLA objectives modal (admin-level roles)
  const [slaForm, setSlaForm] = useState(null);
  const [slaSaving, setSlaSaving] = useState(false);
  const [slaError, setSlaError] = useState(null);

  // Jump-in from the roster (« Productivité » row action)
  useEffect(() => {
    if (initialEmployeeId) setSelectedEmployeeId(initialEmployeeId);
  }, [initialEmployeeId]);

  const selectEmployee = (id) => {
    setSelectedEmployeeId(id);
    if (onEmployeeChange) onEmployeeChange(id);
  };

  const fetchEmployees = useCallback(async () => {
    if (!canBrowseEmployees) return;
    setEmployeesLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/employees/agents/basic', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setEmployees(list);
      setSelectedEmployeeId((prev) => prev || (list.length > 0 ? list[0]._id : ADMIN_OPTION_ID));
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      // The admin's own productivity remains selectable even without employees.
      setSelectedEmployeeId((prev) => prev || ADMIN_OPTION_ID);
    } finally {
      setEmployeesLoading(false);
    }
  }, [canBrowseEmployees]);

  const fetchProductivity = useCallback(async () => {
    const targetId = canBrowseEmployees ? selectedEmployeeId : ownUserId;
    if (!targetId && !isSelf) return;
    if (period === 'custom' && !customStart) return; // wait for a start date
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const qs = params.toString();
      const isAdminSelf = canBrowseEmployees && targetId === ADMIN_OPTION_ID;
      const prodUrl = isSelf
        ? `/api/statistics/me/productivity${qs ? `?${qs}` : ''}`
        : isAdminSelf
          ? `/api/statistics/admin/productivity${qs ? `?${qs}` : ''}`
          : `/api/statistics/employee/${targetId}/productivity${qs ? `?${qs}` : ''}`;
      const insightsUrl = isAdminSelf
        ? `/api/statistics/admin/insights${qs ? `?${qs}` : ''}`
        : `/api/statistics/employee/${targetId}/insights${qs ? `?${qs}` : ''}`;

      const prodPromise = axios.get(prodUrl, { headers });
      // Deep insights (first response, validation latency, reliability…) —
      // admin-level roles only, like /productivity. Soft-fails.
      const insightsPromise = canBrowseEmployees
        ? axios.get(insightsUrl, { headers }).catch(() => null)
        : Promise.resolve(null);
      // Outcomes (tickets handled, resolution rate, sentiment, topics) exist
      // per employee only — the admin-self view keeps activity + insights.
      const perfPromise = !isAdminSelf && canBrowseEmployees
        ? axios.get(`/api/statistics/employee/${targetId}/performance${qs ? `?${qs}` : ''}`, { headers }).catch(() => null)
        : Promise.resolve(null);
      // Team comparator from the roster endpoint (chip periods only).
      const rosterPromise = canBrowseEmployees && period !== 'custom'
        ? axios.get(`/api/employees/roster-stats?window_days=${period}`, { headers }).catch(() => null)
        : Promise.resolve(null);

      const [prodRes, insightsRes, perfRes, rosterRes] = await Promise.all([
        prodPromise, insightsPromise, perfPromise, rosterPromise,
      ]);
      setProductivity(prodRes.data);
      setInsights(insightsRes?.data || null);
      setPerformance(perfRes?.data || null);
      setTeamComparator(rosterRes?.data?.team || null);
    } catch (err) {
      console.error('Failed to fetch employee productivity:', err);
      const status = err?.response?.status;
      if (status === 403 && !isSelf) {
        setError(t('teamStatsUi.prod.forbidden', 'Vous n’avez pas accès à la productivité de ce membre.'));
      } else if (status === 404) {
        setError(t('teamStatsUi.prod.notFound', 'Ce membre n’existe plus dans l’équipe.'));
      } else if (status === 400) {
        setError(t('teamStatsUi.prod.badRequest', 'Ce membre n’a pas pu être identifié.'));
      } else {
        setError(t('teamStatsUi.prod.fetchError', 'Les chiffres de productivité n’ont pas pu être chargés.'));
      }
      setProductivity(null);
      setPerformance(null);
      setInsights(null);
    } finally {
      setIsLoading(false);
    }
  }, [canBrowseEmployees, isSelf, selectedEmployeeId, ownUserId, period, customStart, startDate, endDate, t]);

  const openSlaModal = useCallback(() => {
    const sla = insights?.sla || {};
    setSlaForm({
      first_response_hours: sla.first_response_hours ?? 4,
      draft_validation_hours: sla.draft_validation_hours ?? 8,
      manual_minutes_per_reply: sla.manual_minutes_per_reply ?? 4,
    });
    setSlaError(null);
  }, [insights]);

  const saveSla = useCallback(async () => {
    if (!slaForm) return;
    setSlaSaving(true);
    setSlaError(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put('/api/logistics/team-sla-config', {
        first_response_hours: Number(slaForm.first_response_hours) || null,
        draft_validation_hours: Number(slaForm.draft_validation_hours) || null,
        manual_minutes_per_reply: Number(slaForm.manual_minutes_per_reply) || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSlaForm(null);
      fetchProductivity();
    } catch (err) {
      console.error('Failed to save SLA config:', err);
      setSlaError(t('employeeProductivity.slaSaveError', 'Impossible d’enregistrer les objectifs.'));
    } finally {
      setSlaSaving(false);
    }
  }, [slaForm, fetchProductivity, t]);

  // Drill-down: tickets where the person sent messages of a given category.
  // Roles that browse the team (per member or admin-self), and the signed-in
  // team member on their own tickets (/me/productivity/tickets).
  const [drilldownCategory, setDrilldownCategory] = useState(null);
  const [drilldownTickets, setDrilldownTickets] = useState([]);
  const [drilldownPage, setDrilldownPage] = useState(1);
  const [drilldownTotal, setDrilldownTotal] = useState(0);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState(null);

  const fetchDrilldownTickets = useCallback(
    async (category, page) => {
      if (!category) return;
      if (!isSelf && (!canBrowseEmployees || !selectedEmployeeId)) return;
      setDrilldownLoading(true);
      setDrilldownError(null);
      try {
        const token = localStorage.getItem('access_token');
        const params = new URLSearchParams();
        params.append('category', category);
        params.append('page', String(page));
        params.append('limit', String(DRILLDOWN_PAGE_SIZE));
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        const url = isSelf
          ? `/api/statistics/me/productivity/tickets?${params.toString()}`
          : selectedEmployeeId === ADMIN_OPTION_ID
            ? `/api/statistics/admin/productivity/tickets?${params.toString()}`
            : `/api/statistics/employee/${selectedEmployeeId}/productivity/tickets?${params.toString()}`;
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = response.data || {};
        const list = Array.isArray(data) ? data : data.tickets || data.items || data.results || [];
        const total = typeof data.total === 'number' ? data.total : list.length + (page - 1) * DRILLDOWN_PAGE_SIZE;
        setDrilldownTickets(list);
        setDrilldownTotal(total);
      } catch (err) {
        console.error('Failed to fetch drilldown tickets:', err);
        const status = err?.response?.status;
        setDrilldownError(status === 403
          ? t('teamStatsUi.prod.drilldownForbidden', 'Vous n’avez pas accès à ces tickets.')
          : t('teamStatsUi.prod.drilldownError', 'Les tickets n’ont pas pu être chargés.'));
        setDrilldownTickets([]);
        setDrilldownTotal(0);
      } finally {
        setDrilldownLoading(false);
      }
    },
    [canBrowseEmployees, isSelf, selectedEmployeeId, startDate, endDate, t],
  );

  const openDrilldown = useCallback((category) => {
    if (!canDrill) return;
    setDrilldownCategory(category);
    setDrilldownPage(1);
    fetchDrilldownTickets(category, 1);
  }, [canDrill, fetchDrilldownTickets]);

  const closeDrilldown = useCallback(() => {
    setDrilldownCategory(null);
    setDrilldownTickets([]);
    setDrilldownError(null);
    setDrilldownPage(1);
    setDrilldownTotal(0);
  }, []);

  const changeDrilldownPage = (next) => {
    if (!drilldownCategory || next < 1) return;
    setDrilldownPage(next);
    fetchDrilldownTickets(drilldownCategory, next);
  };

  useEffect(() => {
    closeDrilldown();
  }, [selectedEmployeeId, period, customStart, customEnd, closeDrilldown]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { fetchProductivity(); }, [fetchProductivity]);

  const totalSent = productivity?.total_sent_messages || 0;
  const breakdownRows = useMemo(() => CATEGORIES.map((key) => {
    const meta = CATEGORY_META[key];
    const value = productivity?.[meta.field] || 0;
    return { key, label: t(meta.labelKey, meta.labelFallback), value, series: meta.series, percentage: totalSent > 0 ? (value / totalSent) * 100 : 0 };
  }), [productivity, totalSent, t]);

  const daily = useMemo(() => {
    const rows = fillDailyCounts(productivity?.daily, startDate, endDate || new Date().toISOString().slice(0, 10));
    const max = rows.reduce((m, row) => Math.max(m, row.count || 0), 0);
    return { rows, max };
  }, [productivity, startDate, endDate]);

  const isAdminSelf = selectedEmployeeId === ADMIN_OPTION_ID;
  const selectedName = useMemo(() => {
    if (isSelf) return productivity?.employee_name || userData?.name || userData?.email || '';
    if (isAdminSelf) return userData?.name || t('employeeProductivity.adminSelf', 'Moi (admin)');
    const emp = employees.find((e) => e._id === selectedEmployeeId);
    return emp?.name || emp?.email || productivity?.employee_name || '';
  }, [isSelf, isAdminSelf, selectedEmployeeId, employees, userData, productivity, t]);
  const selectedEmail = isAdminSelf || isSelf
    ? (productivity?.employee_email || userData?.email || '')
    : (productivity?.employee_email || '');
  const selfTiles = isSelf ? selfProductivityTiles(productivity) : null;

  const teamRate = teamComparator?.resolution_rate_window;
  const sla = insights?.sla || null;
  const firstResponse = insights?.first_response || null;
  const validation = insights?.draft_validation || null;
  const closures = insights?.closures || null;
  const reopens = insights?.reopens || null;
  const escalations = insights?.escalations || null;
  const queue = insights?.queue || null;
  const improvements = insights?.improvements || null;

  const aiValidatedTotal = (productivity?.ai_validated_unmodified || 0) + (productivity?.ai_validated_modified || 0);
  const timeSavedSeconds = sla && aiValidatedTotal > 0 ? aiValidatedTotal * (sla.manual_minutes_per_reply || 0) * 60 : null;

  const topicRows = useMemo(() => (performance?.topic_breakdown || [])
    .filter((row) => row.topic)
    .map((row) => ({ label: row.topic, count: row.count, percentage: row.percentage })), [performance]);
  const carrierRows = useMemo(() => (insights?.carrier_mix || [])
    .map((row) => ({ label: row.carrier, count: row.count, percentage: row.percentage })), [insights]);

  // « 28 oct. » — the shared short format (a day key stays that day).
  const fmtDay = (key) => formatShortDate(key, undefined, { empty: key });

  // ------------------------------------------------------------ zones

  const renderToolbar = () => (
    <div className="team-prod__toolbar">
      {canBrowseEmployees && (
        <select
          className="u-select team-prod__select"
          value={selectedEmployeeId}
          onChange={(e) => selectEmployee(e.target.value)}
          disabled={employeesLoading}
          aria-label={t('employeeProductivity.employee', 'Employé')}
        >
          {employeesLoading && employees.length === 0 && (
            <option value="">{t('employeeProductivity.loadingEmployees', 'Chargement…')}</option>
          )}
          <option value={ADMIN_OPTION_ID}>
            {t('employeeProductivity.adminSelf', 'Moi (admin)')}
            {userData?.name ? ` — ${userData.name}` : ''}
          </option>
          {employees.map((emp) => (
            <option key={emp._id} value={emp._id}>{emp.name || emp.email || emp._id}</option>
          ))}
        </select>
      )}
      <Segmented
        label={t('employeeProductivity.period', 'Période')}
        value={period}
        onChange={setPeriod}
        options={[
          ...PERIODS.map((days) => ({ value: days, label: t('teamStatsUi.team.windowDays', '{{days}} jours', { days }) })),
          { value: 'custom', label: t('teamStatsUi.prod.custom', 'Personnalisée') },
        ]}
      />
      {period === 'custom' && (
        <div className="team-prod__dates">
          <input
            className="u-input"
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            max={customEnd || undefined}
            aria-label={t('employeeProductivity.startDate', 'Début')}
          />
          <span aria-hidden="true">→</span>
          <input
            className="u-input"
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            min={customStart || undefined}
            aria-label={t('employeeProductivity.endDate', 'Fin')}
          />
        </div>
      )}
      <span className="team-prod__spacer" />
      {canBrowseEmployees && insights && (
        <button type="button" className="u-btn u-btn--secondary" onClick={openSlaModal}>
          <Target aria-hidden="true" />
          {t('employeeProductivity.slaButton', 'Objectifs')}
        </button>
      )}
    </div>
  );

  const renderIdentity = () => (
    <Card className="team-prod__identity">
      <div className="team-prod__identity-row">
        <span className="u-avatar" aria-hidden="true">{initialsOf(selectedName)}</span>
        <div className="team-prod__who">
          <b>{selectedName}</b>
          {selectedEmail && <span>{selectedEmail}</span>}
        </div>
        {queue && (
          <dl className="team-prod__queue">
            <div>
              <dt>{t('teamStatsUi.prod.queueOpen', 'Tickets ouverts assignés')}</dt>
              <dd className="u-num">{formatNumber(queue.open)}</dd>
            </div>
            {queue.oldest_days != null && (
              <div>
                <dt>{t('teamStatsUi.prod.queueOldest', 'Plus ancien')}</dt>
                <dd className="u-num">{formatDays(queue.oldest_days)}</dd>
              </div>
            )}
            {queue.median_age_days != null && (
              <div>
                <dt>{t('teamStatsUi.prod.queueMedian', 'Âge médian')}</dt>
                <dd className="u-num">{formatDays(queue.median_age_days)}</dd>
              </div>
            )}
          </dl>
        )}
      </div>
      {validation && validation.pending_now > 0 && (
        <Banner tone="warning" title={t('teamStatsUi.prod.pendingTitle', '{{count}} brouillons IA attendent sa validation', { count: formatNumber(validation.pending_now) })}>
          {validation.oldest_pending_at
            ? t('teamStatsUi.prod.pendingSince', 'Le plus ancien attend depuis le {{date}}', { date: formatShortDate(validation.oldest_pending_at) })
            : t('teamStatsUi.prod.pendingHint', 'Ils ne partent qu’une fois validés.')}
        </Banner>
      )}
    </Card>
  );

  const renderKpis = () => (
    <div className="u-kpi-row">
      {selfTiles && (
        <Kpi
          icon={Send}
          label={t('accountScopeUi.prod.kpiSent', 'Messages envoyés')}
          value={formatNumber(selfTiles.sent)}
          context={selfTiles.aiShare != null
            ? t('accountScopeUi.prod.kpiSentContext', 'dont {{share}} % depuis un brouillon IA', { share: selfTiles.aiShare })
            : t('accountScopeUi.prod.kpiPeriod', 'sur la période')}
        />
      )}
      {selfTiles && selfTiles.aiTotal > 0 && (
        <Kpi
          icon={Sparkles}
          tone="success"
          label={t('accountScopeUi.prod.kpiUnmodified', 'Brouillons IA sans retouche')}
          value={selfTiles.unmodifiedShare}
          unit="%"
          context={t('accountScopeUi.prod.kpiUnmodifiedContext', '{{n}} sur {{total}} brouillons IA envoyés', {
            n: formatNumber(selfTiles.unmodified), total: formatNumber(selfTiles.aiTotal),
          })}
          meter={{ value: selfTiles.unmodifiedShare, max: 100, tone: 'success', label: t('accountScopeUi.prod.kpiUnmodified', 'Brouillons IA sans retouche') }}
        />
      )}
      {selfTiles && (
        <Kpi
          icon={Ticket}
          tone="info"
          label={t('accountScopeUi.prod.kpiVisited', 'Tickets consultés')}
          value={formatNumber(selfTiles.ticketsVisited)}
          context={selfTiles.visits > 0
            ? t('accountScopeUi.prod.kpiVisitedContext', '{{duration}} en moyenne par visite', { duration: formatDuration(selfTiles.avgVisit) })
            : t('accountScopeUi.prod.kpiPeriod', 'sur la période')}
        />
      )}
      {!isAdminSelf && performance && (
        <Kpi
          icon={Ticket}
          label={t('employeeProductivity.kpiHandled', 'Tickets traités')}
          value={formatNumber(performance.total_tickets)}
          context={t('teamStatsUi.prod.kpiHandledContext', 'assignés, reçus sur la période')}
        />
      )}
      {!isAdminSelf && performance && (
        <Kpi
          icon={CircleCheck}
          tone="success"
          label={t('employeeProductivity.kpiResolution', 'Taux de résolution')}
          value={Math.round(performance.resolution_rate || 0)}
          unit="%"
          context={teamRate != null
            ? t('employeeProductivity.teamRate', 'équipe : {{rate}} %', { rate: teamRate })
            : t('teamStatsUi.prod.kpiResolutionContext', 'de ses tickets de la période')}
          meter={{ value: performance.resolution_rate || 0, max: 100, tone: 'success', label: t('employeeProductivity.kpiResolution', 'Taux de résolution') }}
        />
      )}
      {firstResponse && firstResponse.median_s != null && (
        <Kpi
          icon={Clock}
          tone="info"
          label={t('employeeProductivity.kpiFirstResponse', '1re réponse (médiane)')}
          value={formatDuration(firstResponse.median_s)}
          context={(
            <>
              <Delta current={firstResponse.median_s} previous={firstResponse.prev_median_s} lowerIsBetter t={t} />
              {firstResponse.sla_share != null && sla
                ? t('employeeProductivity.kpiFirstResponseSub', '{{share}} % ≤ {{hours}} h · {{count}} réponses', {
                  share: firstResponse.sla_share, hours: sla.first_response_hours, count: formatNumber(firstResponse.count),
                })
                : t('employeeProductivity.kpiFirstResponseCount', '{{count}} réponses clients', { count: formatNumber(firstResponse.count) })}
            </>
          )}
        />
      )}
      {validation && validation.median_s != null && (
        <Kpi
          icon={Hourglass}
          tone="info"
          label={t('employeeProductivity.kpiValidation', 'Validation des brouillons IA')}
          value={formatDuration(validation.median_s)}
          context={(
            <>
              <Delta current={validation.median_s} previous={validation.prev_median_s} lowerIsBetter t={t} />
              {validation.sla_share != null && sla
                ? t('employeeProductivity.kpiValidationSub', '{{share}} % ≤ {{hours}} h · {{count}} brouillons validés', {
                  share: validation.sla_share, hours: sla.draft_validation_hours, count: formatNumber(validation.count),
                })
                : t('employeeProductivity.kpiValidationCount', '{{count}} brouillons validés', { count: formatNumber(validation.count) })}
            </>
          )}
        />
      )}
      {!isAdminSelf && performance && performance.avg_sentiment > 0 && (
        <Kpi
          icon={Smile}
          tone={moodTone(performance.avg_sentiment)}
          label={t('employeeProductivity.kpiMood', 'Humeur clients')}
          value={Math.round(performance.avg_sentiment)}
          unit="/100"
          context={t('employeeProductivity.kpiMoodSub', 'sentiment moyen sur ses tickets')}
        />
      )}
    </div>
  );

  const renderOrigin = () => (
    <Card
      icon={Sparkles}
      title={t('employeeProductivity.messageOrigin', 'Origine des réponses envoyées')}
      description={totalSent > 0
        ? t('teamStatsUi.prod.originDesc', '{{sent}} messages envoyés · {{edits}} retouches au total', { sent: formatNumber(totalSent), edits: formatNumber(productivity.total_edits) })
        : null}
      footerNote={timeSavedSeconds != null ? (
        <span
          className="team-prod__saved"
          title={t('employeeProductivity.timeSavedTooltip', 'Estimation : chaque réponse validée depuis un brouillon IA économise le temps de rédaction manuelle défini dans les objectifs ({{minutes}} min).', { minutes: sla.manual_minutes_per_reply })}
        >
          <Sparkles aria-hidden="true" />
          {t('employeeProductivity.kpiTimeSaved', 'Temps gagné grâce à l’IA')}
          {' : '}
          <b>{formatDuration(timeSavedSeconds)}</b>
          {' · '}
          {t('employeeProductivity.kpiTimeSavedSub', '{{count}} réponses IA × {{minutes}} min', {
            count: formatNumber(aiValidatedTotal), minutes: sla.manual_minutes_per_reply,
          })}
        </span>
      ) : null}
    >
      {totalSent > 0 ? (
        <>
          <div className="u-stack team-prod__origin" role="img" aria-label={t('employeeProductivity.messageOrigin', 'Origine des réponses envoyées')}>
            {breakdownRows.filter((row) => row.value > 0).map((row) => (
              <span key={row.key} className={`team-prod__series--${row.series}`} style={{ flexGrow: row.value }} title={`${row.label} — ${formatNumber(row.value)}`} />
            ))}
          </div>
          <ul className="team-prod__origin-rows">
            {breakdownRows.map((row) => {
              const clickable = canDrill && row.value > 0;
              const content = (
                <>
                  <span className={`team-prod__swatch team-prod__series--${row.series}`} aria-hidden="true" />
                  <span className="team-prod__origin-label">{row.label}</span>
                  <span className="team-prod__origin-pct u-num">{`${Math.round(row.percentage)} %`}</span>
                  <b className="u-num">{formatNumber(row.value)}</b>
                  {clickable && <ChevronRight className="team-prod__origin-chevron" aria-hidden="true" />}
                </>
              );
              return (
                <li key={row.key}>
                  {clickable ? (
                    <button
                      type="button"
                      className="team-prod__origin-row is-clickable"
                      onClick={() => openDrilldown(row.key)}
                      title={t('employeeProductivity.viewTickets', 'voir les tickets')}
                    >
                      {content}
                    </button>
                  ) : <div className="team-prod__origin-row">{content}</div>}
                </li>
              );
            })}
          </ul>
        </>
      ) : <p className="team-prod__empty-line">{t('employeeProductivity.noMessages', 'Aucun message envoyé sur la période.')}</p>}
    </Card>
  );

  const renderReliability = () => {
    if (!closures && !reopens && !escalations) return null;
    const closureTotal = closures ? closures.by_human + closures.by_ai + closures.other : 0;
    const quiet = escalations && escalations.count === 0 && reopens && reopens.count === 0 && closureTotal === 0;
    return (
      <Card icon={ListChecks} title={t('teamStatsUi.prod.reliability', 'Fiabilité et réactivité')}>
        {quiet ? (
          <p className="team-prod__empty-line">{t('employeeProductivity.relEmpty', 'Rien à signaler sur la période.')}</p>
        ) : (
          <ul className="team-prod__facts">
            {closures && closureTotal > 0 && (
              <li>
                <span>{t('employeeProductivity.relClosures', 'Clôtures sur ses tickets')}</span>
                <span>
                  <b className="u-num">{formatNumber(closures.by_human)}</b> {t('employeeProductivity.relByTeam', 'par l’équipe')}
                  {' · '}
                  <b className="u-num">{formatNumber(closures.by_ai)}</b> {t('employeeProductivity.relByAi', 'par l’IA')}
                </span>
              </li>
            )}
            {reopens && (
              <li title={t('employeeProductivity.reopensTooltip', 'Réouvertures suite à une réponse client sur un ticket clôturé — mesurées depuis octobre 2026.')}>
                <span>{t('employeeProductivity.relReopens', 'Réouvertures après clôture')}</span>
                <span>
                  {reopens.count > 0 && <TriangleAlert className="team-prod__warn-icon" aria-hidden="true" />}
                  <b className="u-num">{formatNumber(reopens.count)}</b>
                  {reopens.count > 0 && (
                    <> — {t('employeeProductivity.relReopensHuman', '{{count}} après une clôture humaine', { count: reopens.after_human_closure })}</>
                  )}
                </span>
              </li>
            )}
            {escalations && escalations.count > 0 && (
              <li>
                <span>{t('employeeProductivity.relEscalations', 'Escalades reçues de l’IA')}</span>
                <span>
                  <b className="u-num">{formatNumber(escalations.count)}</b>
                  {' · '}
                  {t('employeeProductivity.relEscalationsHandled', '{{handled}} prises en charge', { handled: escalations.handled })}
                  {escalations.median_reaction_s != null && (
                    <> · {t('employeeProductivity.relEscalationsReaction', 'réaction médiane {{duration}}', { duration: formatDuration(escalations.median_reaction_s) })}</>
                  )}
                </span>
              </li>
            )}
            {improvements && improvements.requests > 0 && (
              <li>
                <span>{t('teamStatsUi.prod.improvements', 'Demandes « Améliorer » à l’agent IA')}</span>
                <span>
                  <b className="u-num">{formatNumber(improvements.requests)}</b>
                  {' · '}
                  {t('teamStatsUi.prod.improvementsApplied', '{{count}} appliquées', { count: formatNumber(improvements.applied) })}
                </span>
              </li>
            )}
          </ul>
        )}
      </Card>
    );
  };

  const renderActivity = () => (
    <Card
      icon={ChartColumn}
      title={t('teamStatsUi.prod.dailyTitle', 'Messages envoyés par jour')}
      description={t('teamStatsUi.prod.dailyDesc', '{{time}} passés sur {{tickets}} tickets ({{avg}} en moyenne) · {{visits}} visites', {
        time: formatHoursMinutes(productivity.total_time_on_tickets_seconds),
        tickets: formatNumber(productivity.tickets_visited),
        avg: formatDuration(productivity.avg_time_on_ticket_seconds),
        visits: formatNumber(productivity.visits_count),
      })}
    >
      {daily.max > 0 ? (
        <>
          <div className="team-prod__bars" role="img" aria-label={t('employeeProductivity.dailyActivity', 'Activité par jour — messages envoyés')}>
            {daily.rows.map((row) => (
              <span key={row.date} className="team-prod__bar-slot" title={`${fmtDay(row.date)} — ${formatNumber(row.count)}`}>
                {row.count > 0 && <i style={{ height: `${Math.max(4, (row.count / daily.max) * 100)}%` }} />}
              </span>
            ))}
          </div>
          <div className="team-prod__bars-axis" aria-hidden="true">
            <span>{fmtDay(daily.rows[0].date)}</span>
            <span>{t('teamStatsUi.prod.dailyMax', 'max {{count}} / jour', { count: formatNumber(daily.max) })}</span>
            <span>{fmtDay(daily.rows[daily.rows.length - 1].date)}</span>
          </div>
        </>
      ) : <p className="team-prod__empty-line">{t('employeeProductivity.noMessages', 'Aucun message envoyé sur la période.')}</p>}
    </Card>
  );

  const renderMix = () => {
    if (!topicRows.length && !carrierRows.length) return null;
    return (
      <Card title={t('teamStatsUi.prod.mixTitle', 'Répartition du travail')} description={t('teamStatsUi.prod.mixDesc', 'Motifs et transporteurs de ses tickets sur la période')}>
        <div className="team-prod__mix">
          {topicRows.length > 0 && (
            <div>
              <h3 className="u-overline">{t('employeeProductivity.mixTopics', 'Motifs')}</h3>
              <ProportionList rows={topicRows} />
            </div>
          )}
          {carrierRows.length > 0 && (
            <div>
              <h3 className="u-overline">{t('employeeProductivity.mixCarriers', 'Transporteurs')}</h3>
              <ProportionList rows={carrierRows} />
            </div>
          )}
        </div>
      </Card>
    );
  };

  const renderHeatmap = () => {
    if (!insights?.heatmap || insights.heatmap.length === 0) return null;
    return (
      <Card
        icon={Timer}
        title={t('teamStatsUi.prod.rhythmTitle', 'Rythme')}
        description={t('teamStatsUi.prod.rhythmDesc', 'Messages envoyés par jour et par heure, à votre heure locale')}
      >
        <Heatmap cells={insights.heatmap} t={t} />
      </Card>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <>
          <span className="u-skeleton team-prod__identity-skeleton" aria-hidden="true" />
          <div className="u-kpi-row" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton team-prod__kpi-skeleton" />)}
          </div>
          <span className="team-prod__sr">{t('employeeProductivity.loading', 'Chargement des métriques…')}</span>
        </>
      );
    }
    if (error) return <Banner tone="danger">{error}</Banner>;
    if (!productivity) {
      return (
        <Card>
          <EmptyState icon={ChartColumn} title={t('teamStatsUi.prod.emptyTitle', 'Rien à afficher')}>
            {canBrowseEmployees
              ? t('teamStatsUi.prod.emptyAdmin', 'Choisissez un membre de l’équipe pour voir sa productivité.')
              : t('teamStatsUi.prod.emptyEmployee', 'Vos chiffres apparaissent ici dès que vous traitez des tickets.')}
          </EmptyState>
        </Card>
      );
    }
    const cards = [renderOrigin(), renderReliability(), renderActivity(), renderMix(), renderHeatmap()].filter(Boolean);
    return (
      <>
        {/* The page is theirs (« Ma productivité »): no identity card to tell whose. */}
        {selectedName && !isSelf && renderIdentity()}
        {renderKpis()}
        <div className="team-prod__grid">
          {cards.map((card, i) => <React.Fragment key={i}>{card}</React.Fragment>)}
        </div>
      </>
    );
  };

  const drilldownMeta = drilldownCategory ? CATEGORY_META[drilldownCategory] : null;

  const body = (
    <div className="team-prod">
      {renderToolbar()}
      {renderBody()}

      {slaForm && (
        <Modal
          icon={Target}
          title={t('employeeProductivity.slaTitle', 'Objectifs de l’équipe')}
          description={t('employeeProductivity.slaHint', 'Ces objectifs s’appliquent à toute l’équipe et alimentent les parts « dans l’objectif » de cette page.')}
          onClose={() => !slaSaving && setSlaForm(null)}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setSlaForm(null)} disabled={slaSaving}>
                {t('employeeProductivity.slaCancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={saveSla} disabled={slaSaving}>
                {slaSaving ? t('employeeProductivity.slaSaving', 'Enregistrement…') : t('employeeProductivity.slaSave', 'Enregistrer')}
              </button>
            </>
          )}
        >
          <div className="u-form team-prod__sla">
            <Field label={t('employeeProductivity.slaFirstResponse', 'Première réponse au client (heures)')} htmlFor="team-prod-sla-first">
              <input
                id="team-prod-sla-first"
                className="u-input"
                type="number" min="0.25" max="720" step="0.25"
                value={slaForm.first_response_hours}
                onChange={(e) => setSlaForm({ ...slaForm, first_response_hours: e.target.value })}
              />
            </Field>
            <Field label={t('employeeProductivity.slaValidation', 'Validation d’un brouillon IA (heures)')} htmlFor="team-prod-sla-validation">
              <input
                id="team-prod-sla-validation"
                className="u-input"
                type="number" min="0.25" max="720" step="0.25"
                value={slaForm.draft_validation_hours}
                onChange={(e) => setSlaForm({ ...slaForm, draft_validation_hours: e.target.value })}
              />
            </Field>
            <Field
              label={t('employeeProductivity.slaManualMinutes', 'Temps de rédaction manuelle d’une réponse (minutes)')}
              htmlFor="team-prod-sla-manual"
              help={t('teamStatsUi.prod.slaManualHelp', 'Sert à estimer le temps gagné grâce aux brouillons de l’IA.')}
            >
              <input
                id="team-prod-sla-manual"
                className="u-input"
                type="number" min="0.5" max="60" step="0.5"
                value={slaForm.manual_minutes_per_reply}
                onChange={(e) => setSlaForm({ ...slaForm, manual_minutes_per_reply: e.target.value })}
              />
            </Field>
            {slaError && <Banner tone="danger">{slaError}</Banner>}
          </div>
        </Modal>
      )}

      {drilldownCategory && (
        <Drawer
          wide
          icon={Ticket}
          title={t('teamStatsUi.prod.drilldownTitle', 'Tickets — {{category}}', { category: t(drilldownMeta.labelKey, drilldownMeta.labelFallback) })}
          description={t('teamStatsUi.prod.drilldownDesc', '{{name}} · {{count}} messages sur la période', { name: selectedName, count: formatNumber(drilldownTotal) })}
          onClose={closeDrilldown}
          closeLabel={t('common.close', 'Fermer')}
          footer={drilldownTotal > DRILLDOWN_PAGE_SIZE ? (
            <Pagination
              page={drilldownPage}
              pageSize={DRILLDOWN_PAGE_SIZE}
              total={drilldownTotal}
              onChange={changeDrilldownPage}
              summary={({ first, last, total }) => t('teamStatsUi.prod.pageSummary', '{{first}}–{{last}} sur {{total}}', { first, last, total: formatNumber(total) })}
              previousLabel={t('employeeProductivity.previous', 'Page précédente')}
              nextLabel={t('employeeProductivity.next', 'Page suivante')}
            />
          ) : null}
        >
          {drilldownLoading && (
            <div className="team-prod__drawer-skeleton" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => <span key={i} className="u-skeleton" />)}
            </div>
          )}
          {!drilldownLoading && drilldownError && <Banner tone="danger">{drilldownError}</Banner>}
          {!drilldownLoading && !drilldownError && drilldownTickets.length === 0 && (
            <EmptyState icon={Ticket} title={t('teamStatsUi.prod.noTickets', 'Aucun ticket')}>
              {t('teamStatsUi.prod.noTicketsHint', 'Aucun message de cette origine sur la période choisie.')}
            </EmptyState>
          )}
          {!drilldownLoading && !drilldownError && drilldownTickets.length > 0 && (
            <ul className="u-list team-prod__tickets">
              {drilldownTickets.map((ticket, idx) => {
                const id = ticket?.ticket_id || ticket?._id || ticket?.id || null;
                const status = ticket.ticket_status || ticket.status;
                const sentAt = ticket.sent_at || ticket.last_sent_at || ticket.updated_at;
                return (
                  <li key={ticket.message_id || id || idx}>
                    <button
                      type="button"
                      className="u-row team-prod__ticket"
                      disabled={!id}
                      onClick={() => { closeDrilldown(); navigate(`/tickets/${id}/messages`); }}
                    >
                      <div className="u-row__main">
                        <div className="u-row__title">
                          {ticket.subject || ticket.title || `#${String(id || idx + 1).slice(-6)}`}
                        </div>
                        <div className="u-row__meta">
                          {[ticket.customer_name || ticket.customer_email || ticket.customer, ticket.topic].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <div className="u-row__side">
                        {status && <Badge>{getStatusLabel(t, status)}</Badge>}
                        {sentAt && <span className="u-muted team-prod__sent">{formatShortDateTime(sentAt)}</span>}
                        {id && <ChevronRight aria-hidden="true" className="team-prod__chevron" />}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Drawer>
      )}
    </div>
  );

  if (embedded) return body;
  return (
    <div className="u-page">
      <header className="u-page-header">
        <div className="u-page-header__text">
          <h1 className="u-page-title">{t('createAgent.tabProductivity', 'Productivité')}</h1>
        </div>
      </header>
      <div className="u-well">{body}</div>
    </div>
  );
};

export default EmployeeProductivity;
